import { useState, useEffect, useMemo, useRef, useId } from "react";
import type { ShareDocument, RenderEmailResponse } from "@markeroo/shared";
import { ShareDocumentView } from "../share/ShareDocumentView";
import { Pill } from "@/components/Pill";
import { Dropdown, type DropdownItem } from "@/components/Dropdown";
import { mutedText } from "@/styles/variants";
import { buttonVariants } from "@markeroo/theme";
import { EditSummaryModal } from "./components/EditSummaryModal";
import { copyToClipboard, getShareUrl } from "@/lib/clipboard";
import { useApiClient } from "@/providers/ApiClientProvider";
import { ui, notifications } from "@/strings";
import { shareWithStudent } from "@/strings/labels";
import { useNotification } from "@/providers/NotificationProvider";
import { useModalStack } from "@/providers/ModalStackProvider";
import { useQueryClient } from "@tanstack/react-query";
import PiEnvelopeDefaultStroke from "@markeroo/icons/src/stroke/communication/PiEnvelopeDefaultStroke";
import PiClipboardCheckStroke from "@markeroo/icons/src/stroke/files-&-folders/PiClipboardCheckStroke";
import { getTutorialCompletedSuccessfully } from "@/lib/tutorials/types/window";
import { cleanupTutorialResourcesSafe } from "@/lib/tutorials/cleanup";
import { HighlightErrorBoundary } from "@/components/HighlightErrorBoundary";
import type { TeacherComment } from "@markeroo/shared";
import type { HighlightTag } from "../editor/unified";
import { HighlightNavigationProvider } from "../editor/contexts/HighlightNavigationContext";
import { HighlightNavigationSidebar } from "../editor/components/HighlightNavigationSidebar";
import { EditHighlightModal } from "../editor/components/EditHighlightModal";
import { useCompletedHighlights } from "../editor/hooks/useCompletedHighlights";
import { useHighlightNavigation } from "../editor/contexts/HighlightNavigationContext";
import { useHotkeys } from "../editor/hooks/useHotkeys";
import { useHighlightScroll } from "../editor/hooks/useHighlightScroll";
import type { HighlightWithMetadata } from "../editor/types/highlightNavigation";
import { EditMetricsIndicator } from "./components/EditMetricsIndicator";
import { useDeleteHighlight } from "@/api/hooks";
import { useFeatureFlag } from "@/providers/FeatureFlagProvider";

export interface DocumentMarkupModalProps {
  isOpen: boolean;
  document: ShareDocument | null;
  studentName: string | null;
  isLoading: boolean;
  isError?: boolean;
  onRetry?: () => void;
  onClose: () => void;
  documentId?: string;
  assignmentId?: string;
  /** Optional classId to enable View Edit Replay button */
  classId?: string;
  documentStatus?: "draft" | "final";
  onStatusChange?: (newStatus: "draft" | "final") => void;
  onDocumentUpdate?: (updated: ShareDocument) => void;
  shareToken?: string | null;
  portalKey?: string | null;
  studentEmail?: string | null;
  renderedEmail?: RenderEmailResponse | null;
  isLoadingEmail?: boolean;
  onCreateShare?: (documentId: string) => void;
  isCreatingShare?: boolean;
  /** Callback to open the Writing Analysis modal */
  onOpenAnalysis?: () => void;
}

// Inner component that has access to HighlightNavigationContext
function DocumentMarkupModalContent({
  document,
  studentName,
  isLoading,
  isError,
  onRetry,
  onClose,
  documentId,
  assignmentId,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for Edit Replay feature
  classId,
  documentStatus,
  onStatusChange,
  onDocumentUpdate,
  shareToken,
  portalKey,
  studentEmail,
  renderedEmail,
  isLoadingEmail,
  onCreateShare,
  isCreatingShare,
  onOpenAnalysis,
}: Omit<DocumentMarkupModalProps, "isOpen">) {
  const [isEditSummaryOpen, setIsEditSummaryOpen] = useState(false);
  const [showCopiedFeedback, setShowCopiedFeedback] = useState(false);
  const [showEmailCopiedFeedback, setShowEmailCopiedFeedback] = useState(false);
  const [editingHighlight, setEditingHighlight] = useState<HighlightWithMetadata | null>(null);
  const { show } = useNotification();
  const hasEmail = Boolean(studentEmail);
  const aiDetectorEnabled = useFeatureFlag("ai_detector_extension");

  // Refs for timeout cleanup
  const copyFeedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const emailCopyFeedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (copyFeedbackTimeoutRef.current) {
        clearTimeout(copyFeedbackTimeoutRef.current);
      }
      if (emailCopyFeedbackTimeoutRef.current) {
        clearTimeout(emailCopyFeedbackTimeoutRef.current);
      }
    };
  }, []);

  // Access highlight navigation context
  const { selectedHighlightId, deselectHighlight, navigateNext, navigatePrevious } =
    useHighlightNavigation();

  // Enable scrolling when highlight selected
  useHighlightScroll({
    highlightId: selectedHighlightId,
    enabled: true,
  });

  // Setup keyboard shortcuts
  useHotkeys({
    // Disable global highlight hotkeys while editing a highlight
    enabled: !editingHighlight,
    handlers: [
      {
        key: "Tab",
        handler: (e) => {
          e?.preventDefault();
          navigateNext();
        },
      },
      {
        key: "Tab",
        shift: true,
        handler: (e) => {
          e?.preventDefault();
          navigatePrevious();
        },
      },
      {
        key: "Escape",
        handler: () => {
          if (selectedHighlightId) {
            deselectHighlight();
            const activeElement = window.document.activeElement as HTMLElement | null;
            activeElement?.blur();
          } else {
            onClose();
          }
        },
      },
    ],
  });

  const handleCopyShareLink = async () => {
    if (!shareToken || !portalKey || !documentId) return;
    const url = getShareUrl(portalKey, documentId);
    const success = await copyToClipboard(url);
    if (success) {
      setShowCopiedFeedback(true);
      if (copyFeedbackTimeoutRef.current) {
        clearTimeout(copyFeedbackTimeoutRef.current);
      }
      copyFeedbackTimeoutRef.current = setTimeout(() => {
        setShowCopiedFeedback(false);
        copyFeedbackTimeoutRef.current = null;
      }, 2000);
    }
  };

  const handleCopyEmail = async () => {
    if (!renderedEmail) return;
    try {
      const emailText = `Subject: ${renderedEmail.subject}\n\n${renderedEmail.body}`;
      await navigator.clipboard.writeText(emailText);
      setShowEmailCopiedFeedback(true);
      show(notifications.emailTemplates.emailCopied, "success");
      if (emailCopyFeedbackTimeoutRef.current) {
        clearTimeout(emailCopyFeedbackTimeoutRef.current);
      }
      emailCopyFeedbackTimeoutRef.current = setTimeout(() => {
        setShowEmailCopiedFeedback(false);
        emailCopyFeedbackTimeoutRef.current = null;
      }, 2000);
    } catch {
      show(notifications.emailTemplates.copyFailed, "error");
    }
  };

  const handleSendEmail = () => {
    if (!renderedEmail || !studentEmail) return;
    const subject = encodeURIComponent(renderedEmail.subject);
    const body = encodeURIComponent(renderedEmail.body);
    const mailtoLink = `mailto:${studentEmail}?subject=${subject}&body=${body}`;
    window.open(mailtoLink, "_blank");
  };

  const deleteHighlightMutation = useDeleteHighlight(assignmentId || "", documentId || "");

  const handleDeleteHighlight = (highlight: HighlightWithMetadata) => {
    if (!assignmentId || !documentId) return;

    deleteHighlightMutation.mutate(highlight.id, {
      onSuccess: (updated) => {
        if (onDocumentUpdate) {
          onDocumentUpdate(updated);
        }
      },
      onError: (error) => {
        show(notifications.highlights.removeFailed, "error");
        console.error("Failed to delete highlight:", error);
      },
    });
  };

  return (
    <>
      <div className="flex w-full min-h-[668px] gap-0 overflow-hidden rounded-2xl bg-white shadow-modal flex-col lg:flex-row">
        {/* Main content area */}
        <div className="flex flex-1 min-h-0 flex-col gap-4 sm:gap-6 p-4 sm:p-6 overflow-hidden">
          <header className="flex flex-shrink-0 flex-col gap-3 sm:gap-4 border-b border-theme-primary pb-3 sm:pb-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-lg sm:text-2xl font-semibold text-brand-950">
                    {ui.headers.paperHighlights}
                    {studentName ? (
                      <>
                        {" "}
                        {ui.labels.for}
                        {" "}
                        {studentName}
                      </>
                    ) : ""}
                  </h2>
                  <Pill
                    variant={documentStatus === "final" ? "success" : "info"}
                    icon={documentStatus === "final" ? "✓" : "✎"}
                  >
                    {documentStatus === "draft" ? ui.labels.draft : ui.labels.final}
                  </Pill>
                  {aiDetectorEnabled && assignmentId && documentId && (
                    <EditMetricsIndicator
                      assignmentId={assignmentId}
                      documentId={documentId}
                      onClick={onOpenAnalysis}
                    />
                  )}
                </div>
                {document && (
                  <p className={`${mutedText} text-xs sm:text-sm mt-1`}>
                    {ui.labels.assignment}
                    {": "}
                    {document.assignmentTitle}
                  </p>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2 text-sm sm:flex-shrink-0">
              {onStatusChange && (
                <button
                  type="button"
                  className={`${buttonVariants.secondary} text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2`}
                  onClick={() =>
                    onStatusChange(documentStatus === "draft" ? "final" : "draft")
                  }
                >
                  <span className="hidden sm:inline">
                    {documentStatus === "draft" ? ui.actions.markAsFinal : ui.actions.markAsDraft}
                  </span>
                  <span className="sm:hidden">
                    {documentStatus === "draft" ? ui.labels.final : ui.labels.draft}
                  </span>
                </button>
              )}

              {shareToken && hasEmail && (
                <Dropdown
                  label={ui.actions.sendEmail}
                  disabled={isLoadingEmail || !renderedEmail}
                  items={[
                    {
                      key: "copy",
                      label: showEmailCopiedFeedback ? ui.labels.copied : ui.actions.copyEmail,
                      icon: <PiClipboardCheckStroke size={16} />,
                      onClick: () => { void handleCopyEmail(); },
                    },
                    {
                      key: "send",
                      label: ui.actions.openInEmailClient,
                      icon: <PiEnvelopeDefaultStroke size={16} />,
                      onClick: handleSendEmail,
                    },
                  ] satisfies DropdownItem[]}
                />
              )}

              {shareToken ? (
                <button
                  type="button"
                  className={`${buttonVariants.primary} text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2`}
                  onClick={() => {
                    void handleCopyShareLink();
                  }}
                  data-tutorial-target="copy-share-link-button"
                >
                  <span className="hidden sm:inline">
                    {showCopiedFeedback ? ui.labels.copied : ui.actions.copyShareLink}
                  </span>
                  <span className="sm:hidden">
                    {showCopiedFeedback ? ui.labels.copied : ui.actions.share}
                  </span>
                </button>
              ) : onCreateShare && documentId ? (
                <button
                  type="button"
                  className={`${buttonVariants.primary} text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2`}
                  onClick={() => onCreateShare(documentId)}
                  disabled={isCreatingShare}
                  data-tutorial-target="copy-share-link-button"
                >
                  <span className="hidden sm:inline">
                    {isCreatingShare
                      ? ui.actions.creating
                      : shareWithStudent(studentName?.split(" ")[0] || studentName || "")}
                  </span>
                  <span className="sm:hidden">
                    {isCreatingShare ? ui.actions.creating : ui.actions.share}
                  </span>
                </button>
              ) : null}

              <button
                type="button"
                className={`${buttonVariants.secondary} text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2`}
                onClick={onClose}
              >
                {ui.actions.close}
              </button>
              </div>
            </div>
          </header>

          <div className="flex-1 overflow-y-auto pr-2 [scrollbar-gutter:stable]">
            {isLoading ? (
              <p className={mutedText}>{ui.loading.loadingHighlights}</p>
            ) : isError ? (
              <div className="rounded-xl border border-semantic-error bg-semantic-error-light p-4">
                <p className="font-medium text-semantic-error">{ui.errors.loadHighlightsFailed}</p>
                <p className="mt-1 text-sm text-semantic-error">
                  {ui.errors.loadHighlightsFailedExplanation}
                </p>
                {onRetry && (
                  <button
                    type="button"
                    className={`${buttonVariants.secondary} mt-3`}
                    onClick={onRetry}
                  >
                    {ui.actions.retry}
                  </button>
                )}
              </div>
            ) : document ? (
              <HighlightErrorBoundary>
                <ShareDocumentView
                  document={document}
                  documentId={documentId}
                  assignmentId={assignmentId}
                  documentStatus={documentStatus}
                  isTeacherView={true}
                  onEditSummary={() => setIsEditSummaryOpen(true)}
                  selectedHighlightId={selectedHighlightId}
                  onDeselectHighlight={deselectHighlight}
                />
              </HighlightErrorBoundary>
            ) : (
              <p className={mutedText}>{ui.empty.noHighlightsForPaper}</p>
            )}
          </div>
        </div>

        {/* Sidebar - hidden on mobile, shown on lg+ */}
        {document &&
          (document.tags.length > 0 ||
            (document.teacherComments && document.teacherComments.length > 0)) && (
            <div className="hidden lg:flex">
              <HighlightNavigationSidebar
                isTeacher={true}
                onEditHighlight={(highlight) => setEditingHighlight(highlight)}
                onDeleteHighlight={handleDeleteHighlight}
                tagPalette={document.tagPalette || []}
              />
            </div>
          )}
      </div>

      {/* Edit Summary Modal */}
      {document && assignmentId && documentId && (
        <EditSummaryModal
          isOpen={isEditSummaryOpen}
          onClose={() => setIsEditSummaryOpen(false)}
          document={document}
          assignmentId={assignmentId}
          documentId={documentId}
          onUpdate={(updated) => {
            if (onDocumentUpdate) {
              onDocumentUpdate(updated);
            }
          }}
        />
      )}

      {/* Edit Highlight Modal */}
      {editingHighlight && assignmentId && documentId && (
        <EditHighlightModal
          isOpen={true}
          onClose={() => setEditingHighlight(null)}
          highlight={editingHighlight}
          assignmentId={assignmentId}
          documentId={documentId}
          onSuccess={(updated) => {
            setEditingHighlight(null);
            if (onDocumentUpdate && document) {
              onDocumentUpdate(updated);
            }
          }}
        />
      )}
    </>
  );
}

export function DocumentMarkupModal({
  isOpen,
  document,
  studentName,
  isLoading,
  isError = false,
  onRetry,
  onClose,
  documentId,
  assignmentId,
  classId,
  documentStatus = "draft",
  onStatusChange,
  onDocumentUpdate,
  shareToken,
  portalKey,
  studentEmail,
  renderedEmail,
  isLoadingEmail,
  onCreateShare,
  isCreatingShare,
  onOpenAnalysis,
}: DocumentMarkupModalProps) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const modalId = useId();
  const { registerModal, unregisterModal } = useModalStack();

  // Register modal with stack for scroll locking
  useEffect(() => {
    if (isOpen) {
      const previousElement = window.document.activeElement as HTMLElement;
      registerModal(modalId, previousElement);
      return () => {
        unregisterModal(modalId);
      };
    }
  }, [isOpen, modalId, registerModal, unregisterModal]);

  // Converts document tags to HighlightTag format, filtering by removedLabelIds
  // and showAllMarkup settings (demonstrative vs full tags), memoized for performance
  const tags: HighlightTag[] = useMemo(
    () =>
      document?.tags
        .filter((tag) => {
          const removedSet = new Set(document.removedLabelIds || []);
          const labelId = `${tag.tag_id}-${tag.startPath.join(",")}-${tag.startChar}`;
          if (removedSet.has(labelId)) {
            return false;
          }
          if (!document.showAllMarkup && tag.display_style === "full") {
            return false;
          }
          return true;
        })
        .map((tag) => ({
          tag_id: tag.tag_id,
          startPath: tag.startPath,
          startChar: tag.startChar,
          endPath: tag.endPath,
          endChar: tag.endChar,
          label: tag.label || null,
          color: tag.color || null,
          explanation: tag.explanation,
          suggestion: tag.suggestion || null,
          phrase: tag.phrase || null,
          beforeContext: tag.beforeContext || null,
          afterContext: tag.afterContext || null,
        })) || [],
    [document?.tags, document?.removedLabelIds, document?.showAllMarkup]
  );

  const teacherComments: TeacherComment[] = useMemo(
    () => document?.teacherComments || [],
    [document?.teacherComments]
  );

  const completedHighlightIds = useMemo(
    () => document?.completedHighlightIds || [],
    [document?.completedHighlightIds]
  );

  // Setup completed highlights hook
  const { completedIds, handleCompletedIdsChange } = useCompletedHighlights({
    assignmentId: assignmentId || "",
    documentId: documentId || "",
    initialCompletedIds: completedHighlightIds,
  });


  // Cleanup tutorial resources when modal closes after successful tutorial completion
  useEffect(() => {
    if (!isOpen) {
      const tutorialCompletedSuccessfully = getTutorialCompletedSuccessfully();

      if (tutorialCompletedSuccessfully) {
        void cleanupTutorialResourcesSafe(api, queryClient, {
          invalidateCaches: true,
          clearState: true,
        });
      }
    }
  }, [isOpen, api, queryClient]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-stretch bg-overlay p-4 overflow-auto"
      role="dialog"
      aria-modal="true"
      aria-label={ui.headers.documentMarkup}
      data-tutorial-target="document-markup-modal"
    >
      <HighlightNavigationProvider
        tags={tags}
        teacherComments={teacherComments}
        completedHighlightIds={completedIds}
        onCompletedHighlightsChange={handleCompletedIdsChange}
        isTeacherView={true}
        initialShowCompleted={true}
      >
        <DocumentMarkupModalContent
          document={document}
          studentName={studentName}
          isLoading={isLoading}
          isError={isError}
          onRetry={onRetry}
          onClose={onClose}
          documentId={documentId}
          assignmentId={assignmentId}
          classId={classId}
          documentStatus={documentStatus}
          onStatusChange={onStatusChange}
          onDocumentUpdate={onDocumentUpdate}
          shareToken={shareToken}
          portalKey={portalKey}
          studentEmail={studentEmail}
          renderedEmail={renderedEmail}
          isLoadingEmail={isLoadingEmail}
          onCreateShare={onCreateShare}
          isCreatingShare={isCreatingShare}
          onOpenAnalysis={onOpenAnalysis}
        />
      </HighlightNavigationProvider>
    </div>
  );
}
